import {
  Component, EventEmitter, ChangeDetectorRef,
  Input, Output, ElementRef, AfterViewInit, ViewChild, OnDestroy
} from '@angular/core';
import { Tab } from '../../interface/tab';
import { ContextMenuItem } from '@farris/ui-context-menu';
import { of } from 'rxjs';
import { TabsHandler } from '../../interface/handler/tabs-handler';
import PerfectScrollbar from 'perfect-scrollbar';
import { addResizeListener, removeResizeListener } from '../../utils/resize-event';
import { GLOBAL_TAB_CSS } from './dynamic-load-css';

/**
 * 标签页组件
 */
@Component({
  selector: 'ide-tabs',
  templateUrl: './ide-tabs.component.html',
})
export class IdeTabsComponent implements TabsHandler, AfterViewInit, OnDestroy {

  /** 上下文菜单 */
  @Input() contextMenuItems: ContextMenuItem[];

  /** 页签的最大宽度，单位：px */
  @Input() set maxTabWidth(maxTabWidth: number) {
    this._maxTabWidth = maxTabWidth;
    if (maxTabWidth >= 100) {
      this.tabStyle = { maxWidth: `${maxTabWidth}px` };
    } else {
      this.tabStyle = { maxWidth: `none` };
    }
    this.cd.detectChanges();
  }

  /** 标签页数据源 */
  @Input() tabs: Tab[] = [];

  /** 选中事件，在完成当前标签切换之后触发 */
  @Output() selected = new EventEmitter<Tab>();
  /** 取消选中事件 */
  @Output() unselected = new EventEmitter<Tab>();
  /** 关闭前事件 */
  @Output() beforeClose = new EventEmitter<Tab>();
  /** 选中前事件，在进行当前标签切换之前触发 */
  @Output() selecting = new EventEmitter<Tab>();

  /** 光标是否在标签的关闭按钮上 */
  mouseInCloseIcon = false;

  get maxTabWidth(): number {
    return this._maxTabWidth;
  }

  _maxTabWidth: number = -1;

  /** 页签样式 */
  tabStyle: any = {};

  protected scrollBar: PerfectScrollbar;
  protected scrollOptions: PerfectScrollbar.Options = {
    minScrollbarLength: 35,
    suppressScrollY: true,
    useBothWheelAxes: true
  };

  /** 标签页外层元素，用于附加滚动条组件 */
  @ViewChild("tabItems") containerEl: ElementRef;
  /** 标签页列表元素，用于监听大小变化 */
  @ViewChild("tabList") tabListEl: ElementRef;

  private initContainerScrollbar(): void {
    this.scrollBar = new PerfectScrollbar(this.containerEl.nativeElement, this.scrollOptions);
    addResizeListener(this.containerEl.nativeElement, this.handleResize);
    addResizeListener(this.tabListEl.nativeElement, this.handleResize);
  }

  private handleResize = () => {
    this.scrollBar && this.scrollBar.update();
  };

  /** 当前处于被选中状态的标签实例 */
  get currentTab(): Tab {
    if (this.tabs.length) {
      return this.tabs.find(tab => tab.active);
    }
    return undefined;
  }

  constructor(
    private el: ElementRef,
    private cd: ChangeDetectorRef
  ) { }

  handleMouseEnter(): void {
    this.mouseInCloseIcon = true;
    this.cd.detectChanges();
  }
  handleMouseLeave(): void {
    this.mouseInCloseIcon = false;
    this.cd.detectChanges();
  }

  ngAfterViewInit(): void {
    this.initContainerScrollbar();
  }

  ngOnDestroy(): void {
    removeResizeListener(this.containerEl.nativeElement, this.handleResize);
    removeResizeListener(this.tabListEl.nativeElement, this.handleResize);
  }

  /** 处理标签点击事件 */
  onTabClick(tab: Tab, $event: Event) {
    this.selecting.emit(tab);
    this.select(tab, $event);
  }

  /** 通过id选中标签 */
  selectById(tabId: string) {
    const tab = this.getTab(tabId);
    this.select(tab);
  }

  /** 通过标签实例选中标签 */
  select(tab: Tab, $event?: Event) {
    if (!tab || this.currentTab === tab) {
      return;
    }
    // 取消之前被选中的标签
    if (this.currentTab) {
      this.currentTab.active = false;
      this.unselected.emit(this.currentTab);
    }
    // 选中新的标签
    tab.active = true;
    this.intoView(tab);
    this.selected.emit(tab);
    this.cd.detectChanges();
  }

  /** 将标签滚动到可视区域内 */
  intoView(tab: Tab) {
    const elId = tab.id + '-link';
    const tabHeaderEl = document.getElementById(elId);
    if (tabHeaderEl) {
      tabHeaderEl.scrollIntoView();
    }
  }

  /** 按照被拖拽后的实际顺序返回标签实例数组 */
  private getOrderedTabs() {
    const items = Array.from(this.el.nativeElement.querySelectorAll('.smooth-dnd-draggable-wrapper'));
    if (items && items.length) {
      const tabIds = items.map((n: any) => n.id);
      return tabIds.map(t => this.getTab(t));
    }
    return this.tabs;
  }

  /**
   * 添加一个新的标签
   * @param tab 标签实例
   * @param active 是否设置为被选中状态
   */
  public addTab(tab: Tab, active: boolean = true) {
    if (!this.tabs || !this.tabs.length || this.tabs.length === 1) {
      this.tabs.push(tab);
    } else {
      this.tabs = this.getOrderedTabs();
      // 在当前被选中标签之后添加新的标签
      const activeIndex = this.getTabIndex(this.currentTab.id);
      this.tabs.splice(activeIndex + 1, 0, tab);
    }
    if (active) {
      this.select(tab);
    }
    this.intoView(tab);
    this.cd.markForCheck();
    this.cd.detectChanges();
  }

  private clearTabId(id: string) {
    if (id) {
      const lastIdx = id.lastIndexOf('-link');
      if (lastIdx > -1) {
        return id.substring(0, lastIdx);
      }
      return id;
    }
    return id;
  }

  getTab(id: string) {
    id = this.clearTabId(id);
    return this.tabs.find(n => n.id === id);
  }

  getTabIndex(id: string) {
    id = this.clearTabId(id);
    return this.tabs.findIndex(n => n.id === id);
  }

  removeTab(tabId: string) {
    const index = this.getTabIndex(tabId);
    this.tabs = this.tabs.filter(n => n.id !== tabId);
    // 如果右侧存在标签页则优先打开右侧的，否则选中左侧的
    if (this.tabs.length > index) {
      this.select(this.tabs[index]);
    } else if (index - 1 > -1) {
      this.select(this.tabs[index - 1]);
    }
    this.mouseInCloseIcon = false;
    this.cd.detectChanges();
  }

  /** 关闭全部标签 */
  public closeAll() {
    this.tabs = [];
    this.mouseInCloseIcon = false;
    this.cd.detectChanges();
  }

  /** 触发标签关闭事件 */
  onCloseTab(tab: Tab, event: Event) {
    event.stopPropagation();
    this.beforeClose.emit(tab);
    this.mouseInCloseIcon = false;
  }

  onTabItemDrop($event) {
    const { removedIndex, addedIndex } = $event;
    if (removedIndex !== null && addedIndex !== null && removedIndex !== addedIndex) {
      const items = Array.from(this.el.nativeElement.querySelectorAll('.smooth-dnd-draggable-wrapper'));
      items[removedIndex]['remove']();
      let m = 'after';
      if (removedIndex > addedIndex) {
        m = 'before';
      }
      items[addedIndex][m](items[removedIndex]);
      // 移动后，设置该标签为当前标签
      this.selectById(items[removedIndex]['id']);
    }
  }

  /** 上下文菜单展示前处理 */
  onBeforeShowContextMenu = (e: any) => {
    const { contextMenuDom, event } = e;
    const tabId = event.target.closest('li').id;
    if (contextMenuDom && this.contextMenuItems && this.contextMenuItems.length) {
      const tab = this.getTab(tabId);
      return of({ show: true, data: { tab, tabsRef: this } });
    }
    return of({ show: false });
  }

  /**
   * 关闭其它所有标签
   * @param ignoreTab 标签实例
   */
  public closeOtherTabs(ignoreTab: Tab) {
    this.tabs = this.tabs.filter(n => n.id === ignoreTab.id);
    this.select(ignoreTab);
  }

  /** 触发变更检测 */
  public detectChanges(): void {
    this.cd.markForCheck();
    this.cd.detectChanges();
  }

  public globalInit(): void {
    const headEl: HTMLHeadElement = document.head || document.getElementsByTagName('head')[0];
    if (!headEl) {
      return;
    }
    try {
      const styleEl = document.createElement("style");
      styleEl.type = "text/css";
      styleEl.textContent = GLOBAL_TAB_CSS;
      headEl.appendChild(styleEl);
    } catch (err) { }
  }

}
